import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bell, Check, Clock, AlertTriangle, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useState, useEffect } from 'react';
import { useAuth } from "@/context/AuthContext";
import { IoCheckmarkDone } from "react-icons/io5";

const notificationTypes = {
    success: {
        icon: Check,
        className: "text-green-500 bg-green-500/10",
    },
    warning: {
        icon: AlertTriangle,
        className: "text-yellow-500 bg-yellow-500/10",
    },
    error: {
        icon: AlertTriangle,
        className: "text-red-500 bg-red-500/10",
    },
    info: {
        icon: Info,
        className: "text-blue-500 bg-blue-500/10",
    },
};

const NotificationItem = ({ notification, onRead, setOpen }) => {
    const navigate = useNavigate();
    const { axiosInstance, setUnreadCount } = useAuth();
    const { icon: Icon, className } = notificationTypes[notification.type] || notificationTypes.info;
    
    const handleClick = async () => {
        if (!notification.read) {
            try {
                // Optimistically update the UI
                setUnreadCount(prev => Math.max(0, prev - 1));
                onRead(notification._id);
                
                // Make API call
                await axiosInstance.post(`/notifications/${notification._id}/mark-read`);
            } catch (error) {
                // Revert optimistic update on error
                setUnreadCount(prev => prev + 1);
                console.error('Error marking notification as read:', error);
            }
        }

        // Handle task navigation based on metadata
        if (notification.metadata?.taskKey) {
            setOpen(false);
            navigate(`/${notification.organizationId.slug}/plan/${notification.metadata.taskKey}`);
        }
    };
    
    return (
        <div 
            onClick={handleClick}
            className={cn(
                "flex items-start gap-4 p-4 rounded-lg transition-colors cursor-pointer",
                "hover:bg-muted/50",
                !notification.read && "bg-primary/5"
            )}
        >
            <div className={cn("rounded-full", className, "p-2")}>
                <Icon className="size-4" />
            </div>
            <div className="flex-1 space-y-1">
                <p className="text-sm font-medium leading-none">{notification.title}</p>
                <p className="text-sm text-muted-foreground">{notification.message}</p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Clock className="size-3" />
                    <time>{new Date(notification.timestamp).toLocaleString()}</time>
                </div>
            </div>
            {!notification.read && (
                <div className="size-2 rounded-full bg-primary" />
            )}
        </div>
    );
};

const NotificationCenter = ({ open, setOpen, onOpenChange }) => {
    const [loading, setLoading] = useState(false);
    const { axiosInstance, notifications, setNotifications, fetchUnreadCount } = useAuth();

    useEffect(() => {
        if (open) {
            fetchNotifications();
            fetchUnreadCount();
        }
    }, [open]);

    const fetchNotifications = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/notifications');
            setNotifications(response.data);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    const markAllAsRead = async () => {
        try {
            await axiosInstance.post('/notifications/mark-all-read');
            setNotifications(prev => 
                prev.map(notif => ({ ...notif, read: true }))
            );
            fetchUnreadCount();
        } catch (error) {
            console.error('Error marking notifications as read:', error);
        }
    };

    const handleNotificationRead = (notificationId) => {
        setNotifications(prev =>
            prev.map(notif =>
                notif._id === notificationId
                    ? { ...notif, read: true }
                    : notif
            )
        );
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl" hideClose>
                <DialogHeader>
                    <div className="flex items-center justify-between space-x-8">
                        <DialogTitle>Notifications</DialogTitle>
                        <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={markAllAsRead}
                            disabled={!notifications.some(n => !n.read)}
                            className="shrink-0 flex items-center gap-2"
                        >
                            <IoCheckmarkDone className="size-4" />
                            Mark all as read
                        </Button>
                    </div>
                </DialogHeader>
                <ScrollArea className="max-h-[80vh]">
                    <div className="space-y-2 pr-4">
                        {loading ? (
                            <div className="flex justify-center py-8">
                                <div className="size-8 rounded-full animate-spin border-4 border-muted border-t-primary" />
                            </div>
                        ) : notifications.length > 0 ? (
                            notifications.map((notification) => (
                                <NotificationItem 
                                    key={notification._id} 
                                    notification={notification}
                                    onRead={handleNotificationRead}
                                    setOpen={setOpen}
                                />
                            ))
                        ) : (
                            <div className="flex flex-col items-center justify-center py-8 text-center">
                                <Bell className="size-8 text-muted-foreground mb-2" />
                                <p className="text-sm text-muted-foreground">
                                    No notifications yet
                                </p>
                            </div>
                        )}
                    </div>
                </ScrollArea>
            </DialogContent>
        </Dialog>
    );
};

export default NotificationCenter;
